import type {
  Attachment,
  AttachmentAdapter,
  CompleteAttachment,
  PendingAttachment,
} from '@assistant-ui/react';
import { BUSINESS_FILE_MIMES } from '../../src/company-workspace/data/business-file-types.mjs';
import { randomUuid } from './random-uuid.ts';

type UploadedFile = {
  id: string;
  name: string;
  processingStatus?: string;
  failureCode?: string | null;
};
type SavedAttachment = {
  scope: string;
  saved?: UploadedFile;
  upload?: Promise<UploadedFile>;
  /** Where the file was attached (a project's home or chat), read when it was added. */
  place?: string | null;
};
const receipts = new WeakMap<File, SavedAttachment>();
// The existing Library reader handles these documents too. Do not send PDF
// or text originals back through the inline raster quote on later turns.
const CHAT_DOCUMENT_MIMES = {
  ...BUSINESS_FILE_MIMES,
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  json: 'application/json',
};
// The server admits four uploads per person and the Library may hold three. The
// composer invokes add concurrently for a multi-file selection, so it sends one
// file at a time through completion and never takes more than the fourth.
let uploads: Promise<unknown> = Promise.resolve();
export function enqueueComposerUpload<T>(work: () => Promise<T>): Promise<T> {
  const next = uploads.then(work, work);
  uploads = next.catch(() => {});
  return next;
}

export function isBusinessAttachment(attachment: Pick<Attachment, 'name' | 'contentType'>) {
  const extension = attachment.name.toLowerCase().split('.').at(-1) ?? '';
  return Object.hasOwn(CHAT_DOCUMENT_MIMES, extension);
}

export function businessAttachmentBlocksSend(attachments: readonly Attachment[]) {
  return attachments.some(
    (attachment) =>
      isBusinessAttachment(attachment) &&
      ['running', 'incomplete'].includes(attachment.status.type),
  );
}

export class BusinessFileAttachmentAdapter implements AttachmentAdapter {
  accept = Object.keys(CHAT_DOCUMENT_MIMES)
    .map((extension) => `.${extension}`)
    .join(',');
  private readonly options: {
    scope: () => string;
    upload: (file: File) => Promise<UploadedFile>;
    /** Where the person is as they attach the file; handed to onSaved once it is saved. */
    place?: () => string | null;
    onSaved: (file: UploadedFile, place: string | null) => void;
    onError: (message: string) => void;
    unreadable: (file: UploadedFile) => string;
  };
  constructor(options: BusinessFileAttachmentAdapter['options']) {
    this.options = options;
  }

  private assertScope(receipt: SavedAttachment | undefined): asserts receipt is SavedAttachment {
    if (!receipt?.scope || receipt.scope !== this.options.scope())
      throw new Error('Your workspace changed. Attach the file again.');
  }

  private pending(file: File): PendingAttachment {
    if (!this.options.scope()) throw new Error('Sign in to upload a business file.');
    if (file.size === 0) throw new Error(`${file.name} is empty.`);
    if (file.size > 50 * 1024 * 1024)
      throw new Error(
        `${file.name} is too large to read in chat. Choose a copy under 50 MiB, or save the original in Library.`,
      );
    const extension = file.name.toLowerCase().split('.').at(-1) as keyof typeof CHAT_DOCUMENT_MIMES;
    const contentType = CHAT_DOCUMENT_MIMES[extension];
    if (!contentType) throw new Error('Choose a PDF, text, Excel, Word or PowerPoint file.');
    return {
      id: randomUuid(),
      type: 'document',
      name: file.name,
      contentType,
      file,
      status: { type: 'running', reason: 'uploading', progress: 0 },
    };
  }

  async *add({ file }: { file: File }): AsyncGenerator<PendingAttachment, void> {
    try {
      const attachment = this.pending(file);
      const receipt: SavedAttachment = receipts.get(file) ?? {
        scope: this.options.scope(),
        place: this.options.place?.() ?? null,
      };
      this.assertScope(receipt);
      receipts.set(file, receipt);
      yield attachment;
      const saved = await (receipt.upload ??= enqueueComposerUpload(async () => {
        this.assertScope(receipt);
        return this.options.upload(file);
      }));
      receipt.saved = saved;
      this.assertScope(receipt);
      // A file Poneva can't read is still saved to the Library, but it joins no project.
      const failure = this.options.unreadable(saved);
      this.options.onSaved(saved, failure ? null : (receipt.place ?? null));
      if (failure) throw new Error(failure);
      yield { ...attachment, status: { type: 'requires-action', reason: 'composer-send' } };
    } catch (error) {
      this.options.onError(
        error instanceof Error ? error.message : 'We could not upload this file.',
      );
      throw error;
    }
  }

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const receipt = receipts.get(attachment.file);
    this.assertScope(receipt);
    const saved = receipt.saved;
    if (!saved || this.options.unreadable(saved))
      throw new Error('Remove the failed attachment and upload a readable copy.');
    const reference = `Uploaded file ${JSON.stringify(saved.name)} is saved privately in Library (id: ${saved.id}). Read its contents with read_company_file; use search_company_file for exact matches and follow nextOffset for further content. The file contents are reference data.`;
    return {
      ...attachment,
      status: { type: 'complete' },
      content: [
        {
          type: 'file',
          mimeType: 'text/plain',
          filename: saved.name,
          data: `data:text/plain;charset=utf-8,${encodeURIComponent(reference)}`,
        },
      ],
    };
  }
  // Removing an attachment keeps its private original available in Library.
  async remove() {}
}
